<template>
  <v-container fluid class="outcomes-workspace">
    <div class="workspace-heading">
      <div>
        <div class="workspace-eyebrow">LSA COMMAND</div>
        <h1>Lead outcomes</h1>
        <p>Close the loop from inquiry to job and revenue.</p>
      </div>
      <span class="review-pill">Review mode · live reminders off</span>
    </div>
    <p v-if="queue && queue.capture_mode === 'manual'" class="manual-notice">
      Outcomes are entered by your team. No CRM connection is required for this
      workflow.
    </p>
    <p v-if="error" role="alert" class="outcome-error">{{ error }}</p>
    <section class="workspace-report">
      <h2>Conversion report</h2>
      <div class="report-dates">
        <label
          >Lead created from (UTC)<input v-model="from" type="date" /></label
        ><label>Through (UTC)<input v-model="to" type="date" /></label
        ><label
          >As of (UTC)<input
            v-model="asOf"
            type="datetime-local"
            @input="asOfLive = false"
            step="1" /></label
        ><button @click="loadReport">Update report</button>
        <button class="outcome-secondary" @click="useNow">
          Use current time
        </button>
      </div>
      <p class="muted">
        Based on deduplicated lead-created cohorts and information received by
        the as-of time. Historical conversion labels are not treated as verified
        outcomes.
      </p>
      <div v-if="report" class="metric-grid">
        <div>
          <strong>{{ percent(report.conversion) }}</strong
          ><span>Conversion · hired / all leads</span>
        </div>
        <div>
          <strong>{{ percent(report.outcome_coverage) }}</strong
          ><span>Outcome coverage · resolved / all</span>
        </div>
        <div>
          <strong>{{ percent(report.resolved_win_rate) }}</strong
          ><span>Resolved win rate · hired / resolved</span>
        </div>
      </div>
      <p v-if="report">
        {{ report.total_leads }} leads ·
        <span v-for="(count, state) in report.states" :key="state"
          >{{ labels[state] }} {{ count }} · </span
        >{{ report.completed_jobs }} completed jobs
      </p>
      <div
        v-if="report && Object.keys(report.amounts_by_currency).length"
        class="table-scroll"
      >
        <table>
          <caption>
            Attributed job amounts by currency
          </caption>
          <thead>
            <tr>
              <th>Currency</th>
              <th>Estimated</th>
              <th>Contracted</th>
              <th>Invoiced, net</th>
              <th>Collected, net</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(values, currency) in report.amounts_by_currency"
              :key="currency"
            >
              <td>{{ currency }}</td>
              <td>{{ money(values.estimated_minor) }}</td>
              <td>{{ money(values.contracted_minor) }}</td>
              <td>{{ money(values.invoiced_minor) }}</td>
              <td>{{ money(values.collected_minor) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="report" class="muted">
        Jobs missing values: estimated
        {{ report.jobs_missing_amounts.estimated }}, contracted
        {{ report.jobs_missing_amounts.contracted }}, invoiced
        {{ report.jobs_missing_amounts.invoiced }}, collected
        {{ report.jobs_missing_amounts.collected }}. Hired leads without job
        details: {{ report.won_leads_without_jobs }}.
      </p>
    </section>
    <section class="workspace-queue">
      <div class="workspace-heading">
        <h2>Needs outcome</h2>
        <label
          ><input v-model="all" type="checkbox" @change="loadQueue" /> Include
          resolved leads</label
        ><button @click="loadQueue">Refresh</button>
        <button
          class="outcome-secondary"
          :disabled="digestLoading"
          @click="previewDigest"
        >
          {{ digestLoading ? 'Preparing preview…' : 'Preview weekly digest' }}
        </button>
      </div>
      <p v-if="queue" class="muted">
        Proposed digest: Tuesday 09:00
        {{ queue.timezone || '(timezone unverified)' }}.
        {{ queue.digest.blocked_reason }}
      </p>
      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Lead</th>
              <th>Outcome</th>
              <th>Owner</th>
              <th>Review</th>
              <th>Outcome source / delivery</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pageRows" :key="row.lead_id">
              <td>
                <strong>{{ row.name || 'Lead #' + row.lead_id }}</strong
                ><small
                  >{{ row.job_type || 'Service not recorded' }} · #{{
                    row.lead_id
                  }}</small
                >
              </td>
              <td>
                {{ labels[row.outcome.state]
                }}<small v-if="row.outcome.legacy_review_required"
                  >Legacy conversion needs confirmation</small
                >
              </td>
              <td>
                {{ row.outcome.owner_id ? 'Account owner' : 'Unassigned' }}
              </td>
              <td>
                <span :class="{ 'outcome-error': row.escalated }"
                  >{{
                    row.escalated
                      ? 'Escalated · '
                      : row.snoozed
                      ? 'Snoozed · '
                      : row.due
                      ? 'Due · '
                      : ''
                  }}{{
                    row.snoozed
                      ? row.outcome.snoozed_until
                      : row.outcome.next_review_at || 'Resolved'
                  }}</span
                ><small>UTC</small>
              </td>
              <td>
                {{ sourceLabel(row.outcome)
                }}<small>{{
                  row.client_request_suppressed
                    ? 'Client request suppressed'
                    : 'Delivery disabled for review'
                }}</small>
              </td>
              <td>
                <button
                  class="outcome-secondary"
                  @click="selected = row.lead_id"
                >
                  Update
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="queue && !queue.leads.length">No leads need an outcome.</p>
      <div v-if="queue && queue.leads.length > pageSize" class="report-dates">
        <button :disabled="page === 0" @click="page--">Previous</button
        ><span
          >Page {{ page + 1 }} of
          {{ Math.ceil(queue.leads.length / pageSize) }}</span
        ><button
          :disabled="(page + 1) * pageSize >= queue.leads.length"
          @click="page++"
        >
          Next
        </button>
      </div>
    </section>
    <v-dialog v-model="dialog" max-width="760" scrollable
      ><v-card
        ><v-card-title
          >Lead #{{ selected }}<v-spacer /><v-btn text @click="selected = null"
            >Close</v-btn
          ></v-card-title
        ><v-card-text
          ><lead-outcome-editor
            v-if="selected"
            :lead-id="selected"
            :show-queue="false"
            @saved="changed" /></v-card-text></v-card
    ></v-dialog>
    <v-dialog v-model="digestDialog" max-width="850">
      <v-card v-if="digest">
        <v-card-title
          >Weekly digest preview<v-spacer /><v-btn
            text
            @click="digestDialog = false"
            >Close preview</v-btn
          ></v-card-title
        >
        <v-card-text>
          <p><strong>Subject:</strong> {{ digest.subject }}</p>
          <p>
            Due unresolved leads: {{ digest.eligible_count }} · Preview only; no
            message has been sent.
          </p>
          <iframe
            title="Weekly outcome digest preview"
            :srcdoc="digest.html"
            sandbox=""
            class="digest-frame"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import axios from 'axios'
import LeadOutcomeEditor from '@/components/common/LeadOutcomeEditor.vue'
export default {
  name: 'LeadOutcomes',
  components: { LeadOutcomeEditor },
  data() {
    const now = new Date().toISOString()
    return {
      queue: null,
      report: null,
      error: '',
      all: false,
      selected: null,
      from: now.slice(0, 4) + '-01-01',
      to: now.slice(0, 10),
      asOf: now.slice(0, 19),
      asOfLive: true,
      page: 0,
      pageSize: 25,
      digest: null,
      digestDialog: false,
      digestLoading: false,
      labels: {
        unknown: "Don't know",
        pending: 'Still working it',
        booked: 'Booked',
        won: 'Hired us',
        lost: 'Did not hire us',
      },
    }
  },
  computed: {
    dialog: {
      get() {
        return !!this.selected
      },
      set(value) {
        if (!value) this.selected = null
      },
    },
    pageRows() {
      return this.queue
        ? this.queue.leads.slice(
            this.page * this.pageSize,
            (this.page + 1) * this.pageSize
          )
        : []
    },
  },
  mounted() {
    if (!this.$store.state.auth.jwt) {
      this.$router.push('/login')
      return
    }
    this.loadQueue()
    this.loadReport()
    if (this.$route.query.lead) this.selected = Number(this.$route.query.lead)
  },
  methods: {
    config(params = {}) {
      return {
        params,
        headers: { Authorization: 'Bearer ' + this.$store.state.auth.jwt },
      }
    },
    url(path) {
      return `${process.env.VUE_APP_API_ENDPOINT}/call-tracking/outcomes/${path}`
    },
    percent(value) {
      return value === null ? 'Unavailable' : (value * 100).toFixed(1) + '%'
    },
    money(value) {
      return value === null ? 'Unknown' : (Number(value) / 100).toFixed(2)
    },
    fail(e) {
      this.error =
        e.response && e.response.status === 404
          ? 'The outcome workflow is not enabled in this environment.'
          : (e.response && e.response.data && e.response.data.message) ||
            'Could not load outcomes.'
    },
    sourceLabel(outcome) {
      if (outcome.authority === 'crm')
        return outcome.reconciled_at
          ? 'CRM reconciled ' + outcome.reconciled_at + ' UTC'
          : 'CRM evidence needs review'
      return outcome.version ? 'Entered by your team' : 'Awaiting staff update'
    },
    async previewDigest() {
      this.digestLoading = true
      try {
        const { data } = await axios.get(
          this.url('digest-preview'),
          this.config()
        )
        this.digest = data
        this.digestDialog = true
      } catch (e) {
        this.fail(e)
      } finally {
        this.digestLoading = false
      }
    },
    async loadQueue() {
      try {
        const { data } = await axios.get(
          this.url('queue'),
          this.config({ all: this.all ? 1 : 0 })
        )
        this.queue = data
        this.page = 0
      } catch (e) {
        this.fail(e)
      }
    },
    async loadReport() {
      try {
        if (this.asOfLive) this.asOf = new Date().toISOString().slice(0, 19)
        this.error = ''
        const { data } = await axios.get(
          this.url('report'),
          this.config({
            from: this.from + ' 00:00:00',
            to: this.to + ' 23:59:59',
            as_of: this.asOf.replace('T', ' ').padEnd(19, ':00'),
          })
        )
        this.report = data
      } catch (e) {
        this.fail(e)
      }
    },
    changed() {
      this.loadQueue()
      this.loadReport()
    },
    useNow() {
      this.asOfLive = true
      this.loadReport()
    },
  },
}
</script>
<style scoped>
.manual-notice {
  padding: 12px 16px;
  background: #edf5f4;
  border-radius: 8px;
  color: #276472;
  font-size: 13px;
}
.digest-frame {
  width: 100%;
  min-height: 460px;
  height: 65vh;
  border: 1px solid #dce5e7;
  border-radius: 8px;
}
.outcomes-workspace {
  max-width: 1440px;
  padding: 28px;
  color: #183039;
}
.workspace-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.workspace-heading h1 {
  font-size: 30px;
  margin: 6px 0;
}
.workspace-heading p {
  color: #60747c;
}
.workspace-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: #276472;
}
.review-pill {
  padding: 8px 12px;
  border-radius: 20px;
  background: #e5f2ee;
  font-size: 12px;
  color: #276452;
}
.workspace-report,
.workspace-queue {
  background: white;
  border: 1px solid #dce5e7;
  border-radius: 12px;
  padding: 22px;
  margin-top: 20px;
}
.outcomes-workspace h2 {
  font-size: 19px;
  margin: 0 0 14px;
}
.report-dates {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
}
.report-dates label {
  font-size: 12px;
  font-weight: 600;
}
.report-dates input {
  display: block;
  border: 1px solid #b9cbcf;
  border-radius: 6px;
  padding: 9px;
  margin-top: 5px;
  font: inherit;
}
.outcomes-workspace button {
  background: #276472;
  border: 1px solid #276472;
  color: white;
  border-radius: 6px;
  padding: 9px 14px;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
}
.outcomes-workspace button.outcome-secondary {
  background: white;
  color: #276472;
}
.outcomes-workspace button:disabled {
  opacity: 0.5;
}
.muted {
  font-size: 12px;
  color: #60747c;
  margin: 12px 0;
}
.metric-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin: 20px 0;
}
.metric-grid div {
  border: 1px solid #e3ebed;
  border-radius: 10px;
  padding: 18px;
}
.metric-grid strong {
  display: block;
  font-size: 30px;
  font-weight: 650;
  color: #276472;
}
.metric-grid span {
  font-size: 12px;
}
.table-scroll {
  overflow: auto;
}
table {
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
}
th {
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  color: #60747c;
  white-space: nowrap;
}
td,
th {
  padding: 13px 10px;
  border-bottom: 1px solid #e3ebed;
}
td {
  min-width: 100px;
}
td small {
  display: block;
  color: #60747c;
  font-size: 11px;
  margin-top: 3px;
}
caption {
  text-align: left;
  font-size: 12px;
  color: #60747c;
  margin: 8px 0;
}
@media (max-width: 700px) {
  .metric-grid {
    grid-template-columns: 1fr;
  }
  .outcomes-workspace {
    padding: 16px;
  }
  .workspace-report,
  .workspace-queue {
    padding: 16px;
  }
}
</style>
